import React, { useCallback } from 'react';

import {
  ArrayField,
  ArrayInput,
  Datagrid,
  FormDataConsumer,
  Form,
  SelectField,
  SelectInput,
  SimpleFormIterator,
  TextField,
  TextInput,
  required,
  useNotify,
  useRecordContext,
  useUpdate,
} from 'react-admin';

import {
  Card,
  CardHeader,
  Divider,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Typography,
  IconButton,
  useMediaQuery,
} from '@mui/material';

import { FieldValues } from 'react-hook-form';

import CreateIcon from '@mui/icons-material/Create';

import { get, isEmpty } from 'lodash';
import useDialogStatus from '../hooks/useDialogStatus';

import { alpha2ToFlagEmoji } from './CountryFlag';

import { countryChoices } from '../constants/countries';
import languages from '../constants/languages';
import { boldDataGridStyle } from '../constants/style/datagridStyles';
import { identifierTypeChoices, identifierTypeChoicesForCountry } from '../constants/identifierTypes';
import NoResults from '../components/layout/NoResults';
import useHiddenUntilHover from '../hooks/useHiddenUntilHover';
import validateNationalIdentifier, { validateNationalIdentifierUniqueCountryAndType } from '../utilities/validators/nationalIdentifier';
import errorNotification from '../utilities/errorNotification';
import theme from '../theme';

export const NationalIdentifierInputFields = ({ source }: { source?: string }) => {
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  return (
    <ArrayInput source={source ?? 'nationalIdentifier'} label={false} fullWidth defaultValue={[{}]}>
      <SimpleFormIterator inline sx={{ '.RaSimpleFormIterator-form': { m: 2, alignItems: 'baseline', flexDirection: isTablet ? 'column' : 'row' } }} disableReordering>
        <SelectInput source="country" helperText={false} choices={countryChoices(languages.EN)} validate={[required()]} />
        <FormDataConsumer>
          {({ scopedFormData }) => (
            scopedFormData?.country ? (
              <SelectInput
                source="type"
                helperText={false}
                choices={identifierTypeChoicesForCountry(scopedFormData.country)}
                validate={[
                  required(),
                  (type, allValues) => validateNationalIdentifierUniqueCountryAndType(
                    type,
                    allValues,
                    scopedFormData.country,
                  ),
                ]}
              />
            ) : null
          )}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ scopedFormData }) => (
            scopedFormData?.country && scopedFormData?.type ? (
              <TextInput
                source="identifier"
                helperText={false}
                validate={[
                  required(),
                  (identifier) => validateNationalIdentifier({
                    country: scopedFormData?.country,
                    type: scopedFormData?.type,
                    identifier,
                  }),
                ]}
              />
            ) : null
          )}
        </FormDataConsumer>
      </SimpleFormIterator>
    </ArrayInput>
  );
};

export const NationalIdentifierSingleField = ({
  limit = 2,
  underline = true,
  label,
  emptyText,
  source = 'nationalIdentifier',
}: {
  limit?: number;
  underline?: boolean;
  label?: string;
  emptyText?: string;
  source?: string;
  sortable?: boolean;
}) => {
  const record = useRecordContext();

  if (!record) return emptyText ?? null;

  const value = get(record, source) as Array<{ country: string; identifier: string }>;

  if (isEmpty(value)) return emptyText ?? null;

  const text = value
    .slice(0, limit)
    .map((item, index, array) => `${alpha2ToFlagEmoji(item.country)} ${item.identifier}${index < array.length - 1 ? ', ' : ''}`);

  return (
    <Typography key={label} sx={underline ? { textDecoration: 'underline' } : {}}>
      {text}
      {value.length > 2 && '...'}
    </Typography>

  );
};

const EditDialog = ({
  showEdit,
  buttonId,
}: {
  showEdit: boolean;
  buttonId: string;
}) => {
  const notify = useNotify();
  const record = useRecordContext();
  const { open, openDialog, closeDialog } = useDialogStatus();

  const [update] = useUpdate();

  const handelSave = useCallback((data: FieldValues) => {
    update('individuals', { id: record?.id, data }, {
      onError: (e) => notify(errorNotification(e), { type: 'error' }),
      onSuccess: closeDialog,
      mutationMode: 'pessimistic',
    });
  }, [closeDialog, record?.id, notify, update]);

  if (!showEdit) return null;

  return (
    <>
      <IconButton size="small" onClick={openDialog} id={buttonId}>
        <CreateIcon color="secondary" fontSize="small" />
      </IconButton>
      <Dialog open={open} onClose={closeDialog} fullWidth maxWidth="lg">
        <Form
          record={{
            nationalIdentifier: record?.nationalIdentifier,
          }}
          onSubmit={handelSave}
        >
          <DialogTitle>National identifiers</DialogTitle>
          <DialogContent>
            <NationalIdentifierInputFields />
          </DialogContent>
          <DialogActions>
            <Button color="error" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" variant="contained">Save</Button>
          </DialogActions>
        </Form>
      </Dialog>
    </>
  );
};

export const NationalIdentifierDataGrid = () => (
  <Datagrid
    sx={boldDataGridStyle}
    bulkActionButtons={false}
    empty={<NoResults text="No data to display." icon={null} />}
  >
    <SelectField choices={countryChoices(languages.EN)} source="country" />
    <TextField source="identifier" />
    <SelectField choices={identifierTypeChoices(languages.EN)} source="type" />
  </Datagrid>
);

const NationalIdentifier = ({ showEdit }: { showEdit: boolean }) => {
  const { hiddenElementId, wrapperSx } = useHiddenUntilHover();
  return (
    <Card
      variant="outlined"
      sx={wrapperSx}
    >
      <CardHeader
        sx={{
          color: 'black',
          fontWeight: 'bold',
          flexWrap: 'wrap',
        }}
        slotProps={{ title: { variant: 'inherit' } }}
        title="National identifiers"
        action={<EditDialog showEdit={showEdit} buttonId={hiddenElementId} />}
      />
      <Divider />
      <ArrayField source="nationalIdentifier">
        <NationalIdentifierDataGrid />
      </ArrayField>
    </Card>
  );
};

export default NationalIdentifier;
